import React, { useEffect, useState, FormEvent, useContext } from "react";
import { Table, Input, Button, Space } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../../Hooks/useAuth";
import { LoansFinancingModel } from "../../../../Models/LoansFinancingModel";
import { BillingProductsModel } from "../../../../Models/BillingProductsModel";
import { GetLoansFinancing, PostLoansFinancing } from "../../../../API/LoansFinancingFinepAPI";
import { GetBillingProducts, PostBillingProducts } from "../../../../API/BillingProductsFinepAPI";
import { convertUSDToBRL } from "../../../../Utils/USDToBRL";
import { RegistrationDocumentsFinepContext } from "../../../../Contexts/RegistrationDocumentsFinepContext";
import TabsHeader from "../../../../Components/Shared/TabsHeader";
import Loader from "../../../../Components/Shared/Loader";
import ModalDelete from "../../../../Components/Shared/ModalDelete";
import "./styles.css";

const LoansAndFinancingTab = () => {
    const [loansFinancing, setLoansFinancing] = useState<LoansFinancingModel[]>([]);
    const [loansFinancingInputed, setLoansFinancingInputed] = useState<LoansFinancingModel>({} as LoansFinancingModel);
    const [billingProducts, setBillingProducts] = useState<BillingProductsModel[]>([]);
    const [billingProductsInputed, setBillingProductsInputed] = useState<BillingProductsModel>({} as BillingProductsModel);
    const [validateFields, setValidateFields] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editingLoanId, setEditingLoanId] = useState<string | null>(null); 
    const [editingBillingId, setEditingBillingId] = useState<string | null>(null);
    const [ showDeleteModal, setShowDeleteModal ] = useState(false);
    const [ itemToDelete, setItemToDelete ] = useState<{ type: "loan" | "billing"; index: number } | null>(null);
    const { changeTab, handleFillTab } = useContext(RegistrationDocumentsFinepContext);
    const { registerId } = useParams();
    const user = useAuth();

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const loansData = await GetLoansFinancing(registerId ?? "");
                setLoansFinancing(loansData.data);
                if (loansData.data.length > 0) handleFillTab(3);
                const billingData = await GetBillingProducts(registerId ?? "");
                setBillingProducts(billingData.data);
                if (billingData.data.length > 0) handleFillTab(3);
            } catch (error) {
                console.error(error);
            }
            setLoading(false);
        }

        fetchData();
    }, [registerId]);

    const handleAddOrEditLoansFinancing = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const newLoan = {
            ...loansFinancingInputed,
            userEmail: user.userEmail,
            isValid: validateFields,
            registerId: registerId ?? "",
        };

        try {
            if (editingLoanId) {
                const updatedLoans = loansFinancing.map((loan) =>
                    loan.id === editingLoanId ? newLoan : loan
                );

                setLoansFinancing(updatedLoans);
                setEditingLoanId(null); 
            } else {
                await PostLoansFinancing(newLoan);
                setLoansFinancing([...loansFinancing, newLoan]);
            }

            setLoansFinancingInputed({} as LoansFinancingModel);
            handleFillTab(3);

        } catch (error) {
            console.error(error);
        }

        setLoading(false);
    };

    const handleAddOrEditBillingProducts = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const newBilling = {
            ...billingProductsInputed,
            userEmail: user.userEmail,
            isValid: validateFields,
            registerId: registerId ?? "",
        };

        try {
            if (editingBillingId) {
                const updatedBillings = billingProducts.map((billing) =>
                    billing.id === editingBillingId ? newBilling : billing
                );

                setBillingProducts(updatedBillings);
                setEditingBillingId(null);

            } else {
                await PostBillingProducts(newBilling);
                setBillingProducts([...billingProducts, newBilling]);
            }
            setBillingProductsInputed({} as BillingProductsModel);
            handleFillTab(3);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    const handleDeleteLoan = (index: number) => {
        const updatedLoans = [...loansFinancing];
        updatedLoans.splice(index, 1);
        setLoansFinancing(updatedLoans);
    };

    const handleDeleteBilling = (index: number) => {
        const updatedBillings = [...billingProducts];
        updatedBillings.splice(index, 1);
        setBillingProducts(updatedBillings);
    };

    const handleEditLoan = (record: LoansFinancingModel) => {

        setLoansFinancingInputed(record);

        setEditingLoanId(record.id ?? null);

    };

    const handleEditBilling = (record: BillingProductsModel) => {
        setBillingProductsInputed(record);
        setEditingBillingId(record.id ?? null);
    };
    
    const openDeleteModal = (type: "loan" | "billing", index: number) => {
        setItemToDelete({ type, index });
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (itemToDelete) {
            if (itemToDelete.type === "loan") {
                handleDeleteLoan(itemToDelete.index);
            } else {
                handleDeleteBilling(itemToDelete.index);
            }
        }
        setShowDeleteModal(false);
        setItemToDelete(null);
    };


    const loansColumns = [
        {
            title: "Instituição",
            dataIndex: "institution",
            key: "institution",
        },

        {
            title: "Modalidade",
            dataIndex: "modality",
            key: "modality",
        },

        {
            title: "Taxas",
            dataIndex: "fees",
            key: "fees",
            render: (text: string) => `${text}%`,
        },

        {
            title: "Prazos",
            dataIndex: "deadlines",
            key: "deadlines",
        },

        {
            title: "Valor Inicial",
            dataIndex: "initialValue",
            key: "initialValue",
            render: (value: string) => convertUSDToBRL(value),
        },

        {
            title: "Amortização",
            dataIndex: "amortization",
            key: "amortization",
            render: (value: string) => convertUSDToBRL(value),
        },

        {
            title: "Saldo Devedor",
            dataIndex: "debitBalance",
            key: "debitBalance",
            render: (value: string) => convertUSDToBRL(value),
        },

        {
            title: "Ações",
            key: "actions",
            render: (_: any, record: LoansFinancingModel, index: number) => (
                <Space>
                    <EditOutlined
                        style={{ color: "#0958D9", cursor: "pointer", fontSize: "18px" }}
                        onClick={() => handleEditLoan(record)}
                    />
                    <DeleteOutlined
                        style={{ color: "#CF1322", cursor: "pointer", fontSize: "18px" }}
                        onClick={() => openDeleteModal("loan", index)}
                    />
                </Space>
            ),
        },
    ];

    const billingColumns = [

        {
            title: "Nome do Produto",
            dataIndex: "name",
            key: "name",
        },

        {
            title: `% Participação ${new Date().getFullYear() - 2}`,
            dataIndex: "secondToLastYear",
            key: "secondToLastYear",
            render: (value: number) => `${value}%`,
        },

        {
            title: `% Participação ${new Date().getFullYear() - 1}`,
            dataIndex: "penultimateYear",
            key: "penultimateYear",
            render: (value: number) => `${value}%`,
        },

        {
            title: `% Participação ${new Date().getFullYear()}`,
            dataIndex: "lastYear",
            key: "lastYear",
            render: (value: number) => `${value}%`,
        },

        {
            title: "Ações",
            key: "actions",
            render: (_: any, record: BillingProductsModel, index: number) => (
                <Space>
                    <EditOutlined
                        style={{ color: "#0958D9", cursor: "pointer", fontSize: "18px" }}
                        onClick={() => handleEditBilling(record)}
                    />
                    <DeleteOutlined
                        style={{ color: "#CF1322", cursor: "pointer", fontSize: "18px" }}
                        onClick={() => openDeleteModal("billing", index)}
                    />
                </Space>
            ),
        },
    ];
    return (
        <>

            {loading && <Loader />}
            <TabsHeader
                canValidate={true}
                valueSelected={validateFields}
                changeValidate={async (isValid: boolean) => {
                    setValidateFields(isValid);
                }}
            />

            <strong className="d-block mt-4 mb-4">Empréstimos e Financiamentos</strong>
            <Table
                columns={loansColumns}
                dataSource={loansFinancing}
                rowKey="institution"
                pagination={false}
                className="custom-table"
                bordered={false}
                footer={() => (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            justifyContent: "space-between",
                            background: "#F8F9FC",
                            padding: "10px",
                            borderRadius: "4px",
                        }}
                    >
                        <div style={{ display: "flex", flex: 1, gap: "8px" }}>
                            <Input
                                placeholder="Instituição"
                                value={loansFinancingInputed.institution}
                                onChange={(e) =>
                                    setLoansFinancingInputed({
                                        ...loansFinancingInputed,
                                        institution: e.target.value,
                                    })
                                }
                            />
                            <Input
                                placeholder="Modalidade"
                                value={loansFinancingInputed.modality}
                                onChange={(e) =>
                                    setLoansFinancingInputed({
                                        ...loansFinancingInputed,
                                        modality: e.target.value,
                                    })
                                }
                            />
                            <Input
                                placeholder="Taxas (%)"
                                value={loansFinancingInputed.fees}
                                onChange={(e) =>
                                    setLoansFinancingInputed({
                                        ...loansFinancingInputed,
                                        fees: e.target.value,
                                    })
                                }
                            />
                            <Input
                                placeholder="Prazos"
                                value={loansFinancingInputed.deadlines}
                                onChange={(e) =>
                                    setLoansFinancingInputed({
                                        ...loansFinancingInputed,
                                        deadlines: e.target.value,
                                    })
                                }
                            />
                            <Input
                                placeholder="Valor Inicial"
                                value={loansFinancingInputed.initialValue}
                                onChange={(e) =>
                                    setLoansFinancingInputed({
                                        ...loansFinancingInputed,
                                        initialValue: e.target.value,
                                    })
                                }
                            />
                            <Input
                                placeholder="Amortização"
                                value={loansFinancingInputed.amortization}
                                onChange={(e) =>
                                    setLoansFinancingInputed({
                                        ...loansFinancingInputed,
                                        amortization: e.target.value,
                                    })
                                }
                            />
                            <Input
                                placeholder="Saldo Devedor"
                                value={loansFinancingInputed.debitBalance}
                                onChange={(e) =>
                                    setLoansFinancingInputed({
                                        ...loansFinancingInputed,
                                        debitBalance: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <Button type="primary" onClick={handleAddOrEditLoansFinancing}>
                            {editingLoanId ? "Salvar Edição" : "Adicionar"}
                        </Button>
                    </div>
                )}
            />

            <strong className="d-block mt-4 mb-4">Faturamento dos Principais Produtos</strong>
            <Table
                columns={billingColumns}
                dataSource={billingProducts}
                rowKey="name"
                pagination={false}
                className="custom-table"
                bordered={false}
                footer={() => (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            justifyContent: "space-between",
                            background: "#F8F9FC",
                            padding: "10px",
                            borderRadius: "4px",
                        }}
                    >
                        <div style={{ display: "flex", flex: 1, gap: "8px" }}>
                            <Input
                                placeholder="Nome do Produto"
                                value={billingProductsInputed.name}
                                onChange={(e) =>
                                    setBillingProductsInputed({
                                        ...billingProductsInputed,
                                        name: e.target.value,
                                    })
                                }
                            />
                            <Input
                                placeholder={`% Participação ${new Date().getFullYear() - 2}`}
                                value={billingProductsInputed.secondToLastYear}
                                onChange={(e) =>
                                    setBillingProductsInputed({
                                        ...billingProductsInputed,
                                        secondToLastYear: parseFloat(e.target.value),
                                    })
                                }
                            />
                            <Input
                                placeholder={`% Participação ${new Date().getFullYear() - 1}`}
                                value={billingProductsInputed.penultimateYear}
                                onChange={(e) =>
                                    setBillingProductsInputed({
                                        ...billingProductsInputed,
                                        penultimateYear: parseFloat(e.target.value),
                                    })
                                }
                            />
                            <Input
                                placeholder={`% Participação ${new Date().getFullYear()}`}
                                value={billingProductsInputed.lastYear}
                                onChange={(e) =>
                                    setBillingProductsInputed({
                                        ...billingProductsInputed,
                                        lastYear: parseFloat(e.target.value),
                                    })
                                }
                            />
                        </div>
                        <Button type="primary" onClick={handleAddOrEditBillingProducts}>
                            {editingBillingId ? "Salvar Edição" : "Adicionar"}
                        </Button>
                    </div>
                )}
            />
            <ModalDelete
                show={showDeleteModal}
                toggleShow={() => setShowDeleteModal(false)}
                setResponse={(response) => {
                    if (response) {
                        confirmDelete();
                    }
                }}
            />
            <div className="d-flex justify-content-end mt-4 mb-4">
                <Button className="me-3" onClick={() => changeTab(2)}>Voltar</Button>
            </div>
        </>
    );
};

export default LoansAndFinancingTab; 